import React, { useState } from 'react';
import { Form, Formik } from 'formik';

import mixpanel from '~/components/CmsMain/mixpanel';
import StepperComponent from '~/components/core/Stepper';
import type { StepProps } from '~/components/core/Stepper/Step';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import type { OrganizationModel } from '~/components/types/organization-types';
import { SOP_MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import { reportAxiosError } from '~/Utils';

import { useSopConfiguration } from './hooks/useSopConfiguration';
import SopDocumentReviewStep from './SopDocumentReviewStep';
import SopTocStep from './SopTocStep';
import SopUploadStep from './SopUploadStep';
import type { UploadData } from './types';

export const PAGE_SEPARATOR = '\n\n---\n\n';

const Stepper = StepperComponent as typeof StepperComponent & {
  Step: React.ComponentType<StepProps>;
};

interface SopConfigurationDialogProps {
  open: boolean;
  onClose: () => void;
  onSaveSuccess?: () => void;
  subOrganizationId?: number;
  subOrganizationName?: string;
  isReplacing?: boolean;
}

const SopConfigurationDialog: React.FC<SopConfigurationDialogProps> = ({
  open,
  onClose,
  onSaveSuccess,
  subOrganizationId,
  subOrganizationName,
  isReplacing = false,
}) => {
  const { organization } = useSysconfig() as { organization: OrganizationModel };

  const [uploadData, setUploadData] = useState<UploadData | null>(null);
  const [markdownPages, setMarkdownPages] = useState<string[] | null>(null);
  const [originalPages, setOriginalPages] = useState<string[] | null>(null);
  const [wasContentTouched, setWasContentTouched] = useState(false);
  const [tocMarkdown, setTocMarkdown] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isOcrLoading, setIsOcrLoading] = useState(false);
  const [isTocLoading, setIsTocLoading] = useState(false);

  const { performOcr, analyze, saveConfiguration } = useSopConfiguration({ subOrganizationId });

  const triggerOcrProcessing = async (storedFileId: number) => {
    try {
      const result = await performOcr(storedFileId);
      setMarkdownPages(result.markdown_pages);
      setOriginalPages(result.markdown_pages.map((page) => page.trim()));
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      setIsProcessing(false);
      setIsOcrLoading(false);
    }
  };

  const handleContentChange = (pages: string[]) => {
    setMarkdownPages(pages);
    if (!wasContentTouched) {
      setWasContentTouched(true);
    }
  };

  const handleUploadComplete = (): Promise<boolean> => {
    if (!uploadData) return Promise.resolve(false);

    setIsProcessing(true);
    setIsOcrLoading(true);
    triggerOcrProcessing(uploadData.stored_file_id);

    return Promise.resolve(true);
  };

  const triggerTocGeneration = async (pages: string[]) => {
    try {
      const result = await analyze(pages);
      setTocMarkdown(result.toc_markdown);
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      setIsProcessing(false);
      setIsTocLoading(false);
    }
  };

  const wasOcrEdited = (): boolean => {
    if (!wasContentTouched || !originalPages || !markdownPages) return false;
    return markdownPages.length !== originalPages.length || markdownPages.some((page, i) => page !== originalPages[i]);
  };

  const handleReviewComplete = (): Promise<boolean> => {
    if (!markdownPages) return Promise.resolve(false);

    if (wasOcrEdited()) {
      mixpanel.track(SOP_MIXPANEL_EVENTS.SOP_OCR_OUTPUT_EDITED, {
        organization_id: organization.id,
        sub_organization_id: subOrganizationId || null,
      });
    }

    setIsProcessing(true);
    setIsTocLoading(true);
    triggerTocGeneration(markdownPages);

    return Promise.resolve(true);
  };

  const handleFinish = async () => {
    if (!markdownPages || !tocMarkdown) return false;

    const eventName = isReplacing
      ? SOP_MIXPANEL_EVENTS.SOP_CONFIGURATION_REPLACED
      : SOP_MIXPANEL_EVENTS.SOP_CONFIGURATION_SAVED;

    setIsProcessing(true);
    try {
      await saveConfiguration({
        markdown_pages: markdownPages,
        toc_markdown: tocMarkdown,
      });
      mixpanel.track(eventName, {
        organization_id: organization.id,
        sub_organization_id: subOrganizationId || null,
      });
      onSaveSuccess?.();
      handleClose();
      return true;
    } catch (error) {
      await reportAxiosError(error);
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (!isProcessing && !isUploading) {
      setUploadData(null);
      setMarkdownPages(null);
      setOriginalPages(null);
      setWasContentTouched(false);
      setTocMarkdown(null);
      onClose();
    }
  };

  if (!open) return null;

  return (
    <Formik initialValues={{}} onSubmit={() => {}}>
      <Form>
        <Stepper
          isDialog
          dialogProps={{
            isDialog: true,
            open: true,
            maxWidth: false,
            PaperProps: {
              style: {
                height: '80vh',
                width: '900px',
              },
            },
            onClose: handleClose,
            title: subOrganizationName
              ? `${subOrganizationName} Standard Operating Procedures Upload & Review`
              : `${organization.name} Standard Operating Procedures Upload & Review`,
          }}
          onComplete={handleFinish}
          onStepChange={() => {}}
          nextBtnText="Next"
          backBtnText="Back"
          finishBtnText="Save"
          actionBtnText=""
          firstStepBackBtnText="Back"
          firstStepNextBtnText="Next"
          contentClassName="flex justify-center p-24"
          stepsContainerClassName="flex-1 flex flex-col"
        >
          <Stepper.Step
            label="Upload Your SOP File"
            onNext={handleUploadComplete}
            onBack={() => {}}
            disableBack={true}
            disableNext={!uploadData || isProcessing || isUploading}
          >
            <SopUploadStep
              onNext={setUploadData}
              onUploadingChange={setIsUploading}
              isUploading={isUploading}
              uploadedFile={uploadData || undefined}
            />
          </Stepper.Step>

          <Stepper.Step
            label="Internal SOP Content Extraction"
            onNext={handleReviewComplete}
            onBack={() => {}}
            disableNext={isProcessing}
            disableBack={isProcessing}
          >
            <SopDocumentReviewStep
              markdown_pages={markdownPages || []}
              onContentChange={handleContentChange}
              isLoading={isOcrLoading}
            />
          </Stepper.Step>

          <Stepper.Step
            label="Review Table of Content"
            onNext={() => {}}
            onBack={() => {}}
            disableNext={isProcessing}
            disableBack={isProcessing}
          >
            <SopTocStep toc_markdown={tocMarkdown || ''} isLoading={isTocLoading} />
          </Stepper.Step>
        </Stepper>
      </Form>
    </Formik>
  );
};

export default SopConfigurationDialog;
